/**
 * useConfig.ts - Hook to access FMwvTool configuration via Configgr
 * 
 * Provides easy access to Configgr for reading and writing settings.
 */

import { useState, useEffect, useCallback } from 'react';
import { getConfiggr } from '@opcentral/dependr';
import { validateConfig } from '../config/fmwvtool.config.schema';

/**
 * Hook to access FMwvTool configuration
 * 
 * @example
 * const { get, set, reset } = useConfig();
 * 
 * // Get a setting
 * const saveMode = get('dataSubmission.saveMode', 'batch');
 * 
 * // Set a setting
 * set('dataSubmission.saveMode', 'auto');
 * 
 * // Reset to default
 * reset('dataSubmission.saveMode');
 */
export function useConfig() {
  const configgr = getConfiggr();
  const [, forceUpdate] = useState({});
  
  /**
   * Get a configuration value
   * @param key - Dot-notation key (e.g., 'dataSubmission.saveMode')
   * @param defaultValue - Default value if not set
   */
  const get = useCallback((key: string, defaultValue?: any) => {
    try {
      const value = configgr.getSetting(`fmwvtool.${key}`);
      return value !== undefined ? value : defaultValue;
    } catch (err) {
      console.warn(`[useConfig] Failed to get ${key}:`, err);
      return defaultValue;
    }
  }, [configgr]);
  
  /**
   * Set a configuration value
   * @param key - Dot-notation key
   * @param value - Value to set
   * @param validate - Whether to validate against schema (default: true)
   */
  const set = useCallback((key: string, value: any, validate = true) => {
    try {
      // Validate if requested
      if (validate) {
        const validation = validateConfig(key, value);
        if (!validation.valid) {
          console.error(`[useConfig] Validation failed for ${key}:`, validation.error);
          return false;
        }
      }
      
      configgr.updateSetting(`fmwvtool.${key}`, value);
      forceUpdate({}); // Trigger re-render
      return true;
    } catch (err) {
      console.error(`[useConfig] Failed to set ${key}:`, err);
      return false;
    }
  }, [configgr]);
  
  /**
   * Reset a configuration value to default
   * @param key - Dot-notation key
   */
  const reset = useCallback((key: string) => {
    try {
      configgr.deleteSetting(`fmwvtool.${key}`);
      forceUpdate({}); // Trigger re-render
      return true;
    } catch (err) {
      console.error(`[useConfig] Failed to reset ${key}:`, err);
      return false;
    }
  }, [configgr]);
  
  /**
   * Get all configuration values
   */
  const getAll = useCallback(() => {
    try {
      return configgr.getAll('fmwvtool');
    } catch (err) {
      console.error('[useConfig] Failed to get all config:', err);
      return {};
    }
  }, [configgr]);
  
  /**
   * Set multiple configuration values at once
   * @param values - Object with key-value pairs
   */
  const setMultiple = useCallback((values: Record<string, any>) => {
    try {
      Object.entries(values).forEach(([key, value]) => {
        set(key, value);
      });
      return true;
    } catch (err) {
      console.error('[useConfig] Failed to set multiple:', err);
      return false;
    }
  }, [set]);
  
  /**
   * Reset all configuration to defaults
   */
  const resetAll = useCallback(() => {
    try {
      configgr.deleteAll('fmwvtool');
      forceUpdate({}); // Trigger re-render
      return true;
    } catch (err) {
      console.error('[useConfig] Failed to reset all:', err);
      return false;
    }
  }, [configgr]);
  
  /**
   * Export configuration as JSON
   */
  const exportConfig = useCallback(() => {
    try {
      const config = getAll();
      return JSON.stringify(config, null, 2);
    } catch (err) {
      console.error('[useConfig] Failed to export config:', err);
      return null;
    }
  }, [getAll]);
  
  /**
   * Import configuration from JSON
   * @param json - JSON string with configuration
   */
  const importConfig = useCallback((json: string) => {
    try {
      const config = JSON.parse(json);
      setMultiple(config);
      return true;
    } catch (err) {
      console.error('[useConfig] Failed to import config:', err);
      return false;
    }
  }, [setMultiple]);
  
  /**
   * Subscribe to configuration changes
   * @param key - Key to watch (or '*' for all)
   * @param callback - Callback function
   */
  const subscribe = useCallback((key: string, callback: (value: any) => void) => {
    const fullKey = key === '*' ? 'fmwvtool' : `fmwvtool.${key}`;
    return configgr.subscribe(fullKey, callback);
  }, [configgr]);
  
  return {
    // Core operations
    get,
    set,
    reset,
    
    // Bulk operations
    getAll,
    setMultiple,
    resetAll,
    
    // Import/Export
    exportConfig,
    importConfig,
    
    // Subscriptions
    subscribe,
    
    // Direct access to configgr
    configgr,
  };
}

/**
 * Hook to watch a specific configuration value
 * @param key - Configuration key to watch
 * @param defaultValue - Default value
 */
export function useConfigValue<T = any>(key: string, defaultValue?: T): [T, (value: T) => void] {
  const { get, set, subscribe } = useConfig();
  const [value, setValue] = useState<T>(() => get(key, defaultValue));
  
  useEffect(() => {
    // Subscribe to changes
    const unsubscribe = subscribe(key, (newValue) => {
      setValue(newValue);
    });
    
    return unsubscribe;
  }, [key, subscribe]);
  
  const setter = useCallback((newValue: T) => {
    set(key, newValue);
    setValue(newValue);
  }, [key, set]);
  
  return [value, setter];
}

export default useConfig;
